import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {}; // module safety

const BASE_URL = "/api";

type CompanyOverviewData = {
  merchant_id: string;
  company_name: string;
  company_name_kana: string;
  postal_code: string;
  address_city: string;
  address_rest: string;
  phone: string;
  fax: string;
  established_date: string;
  industry: string;
  industry_other: string;
  capital: string;
  store_count: string;
  employee_count: string;
  business_hours: string;
  regular_holiday: string;
  is_completed: boolean;
  updated_at?: string;
};

function ReadonlyRow({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <div
        style={{
          fontWeight: 500,
          color: "#334155",
          whiteSpace: "pre-wrap",
          lineHeight: 1.6,
        }}
      >
        {value && String(value).trim() ? String(value) : <span style={{ color: "#94a3b8" }}>—</span>}
      </div>
    </div>
  );
}

function EditRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          fontWeight: 700,
          color: "#0f172a",
          outline: "none",
          background: "#fff",
        }}
      />
    </div>
  );
}



function ReadonlyInputBox({
  label,
  value,
  required,
  unit,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
  unit?: string;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: 44,
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: "0 12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 700,
          }}
        >
          {displayValue}
        </div>

        {unit && (
          <span
            style={{
              padding: "0 12px",
              height: 44,
              display: "flex",
              alignItems: "center",
              borderLeft: "1px solid #e2e8f0",
              background: "#f1f5f9",
              color: "#334155",
              fontSize: 14,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}


export default function StaffFormViewCompanyOverview() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CompanyOverviewData | null>(null);
  const [error, setError] = useState("");

  const [downloading, setDownloading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // editable draft
  const [draft, setDraft] = useState({
    company_name: "",
    company_name_kana: "",
    postal_code: "",
    address_city: "",
    address_rest: "",
    phone: "",
    fax: "",
    established_date: "",
    industry: "",
    industry_other: "",
    capital: "",
    store_count: "",
    employee_count: "",
    business_hours: "",
    regular_holiday: "",
  });

  const load = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");
    setSaveMsg("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form1_company_overview`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d: CompanyOverviewData | null = json?.data || null;
      setData(d);

      // sync draft from fetched data
      setDraft({
        company_name: d?.company_name || "",
        company_name_kana: d?.company_name_kana || "",
        postal_code: d?.postal_code || "",
        address_city: d?.address_city || "",
        address_rest: d?.address_rest || "",
        phone: d?.phone || "",
        fax: d?.fax || "",
        established_date: d?.established_date || "",
        industry: d?.industry || "",
        industry_other: d?.industry_other || "",
        capital: d?.capital || "",
        store_count: d?.store_count || "",
        employee_count: d?.employee_count || "",
        business_hours: d?.business_hours || "",
        regular_holiday: d?.regular_holiday || "",
      });

      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム①（会社概要）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム1_会社概要_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const startEdit = () => {
    setSaveMsg("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    // restore from last loaded data
    setDraft({
      company_name: data?.company_name || "",
      company_name_kana: data?.company_name_kana || "",
      postal_code: data?.postal_code || "",
      address_city: data?.address_city || "",
      address_rest: data?.address_rest || "",
      phone: data?.phone || "",
      fax: data?.fax || "",
      established_date: data?.established_date || "",
      industry: data?.industry || "",
      industry_other: data?.industry_other || "",
      capital: data?.capital || "",
      store_count: data?.store_count || "",
      employee_count: data?.employee_count || "",
      business_hours: data?.business_hours || "",
      regular_holiday: data?.regular_holiday || "",
    });
    setIsEditing(false);
    setSaveMsg("");
  };

  const saveEdit = async () => {
    if (!merchantId) return;

    try {
      setSaving(true);
      setSaveMsg("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form1_company_overview`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "保存に失敗しました。");

      setSaveMsg("保存しました。");
      setIsEditing(false);

      // reload to refresh updated_at and reflect saved values
      await load();
    } catch (e: any) {
      setSaveMsg(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffLayout title="フォーム① 会社概要（確認）" active="progress">
      {/* Top buttons */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        {!isEditing ? (
          <button
            onClick={startEdit}
            disabled={loading || !!error || !data}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #0f172a",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 900,
              cursor: "pointer",
              opacity: loading || !!error || !data ? 0.6 : 1,
            }}
            title={!data ? "データが存在しないため編集できません" : ""}
          >
            編集
          </button>
        ) : (
          <>
            <button
              onClick={saveEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #16a34a",
                background: "#16a34a",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
              onClick={cancelEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              キャンセル
            </button>
          </>
        )}
      </div>

      {saveMsg && (
        <div
          style={{
            marginBottom: 10,
            background: saveMsg.includes("失敗") ? "#fff1f2" : "#ecfdf5",
            border: saveMsg.includes("失敗") ? "1px solid #fecdd3" : "1px solid #bbf7d0",
            color: saveMsg.includes("失敗") ? "#b91c1c" : "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {saveMsg}
        </div>
      )}

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          {/* status card */}
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data?.is_completed ? "#166534" : "#9a3412" }}>
              {data?.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data?.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
            {isEditing && (
              <div style={{ marginTop: 8, color: "#0f172a", fontWeight: 900 }}>
                ※ スタッフ編集モード
              </div>
            )}
          </div>

          {/* table */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              {isEditing ? "入力内容（編集）" : "入力内容（読み取り専用）"}
            </div>

            {isEditing ? (
              <>
                <EditRow label="会社名" value={draft.company_name} onChange={(v) => setDraft((p) => ({ ...p, company_name: v }))} />
                <EditRow
                  label="会社名（カナ）"
                  value={draft.company_name_kana}
                  onChange={(v) => setDraft((p) => ({ ...p, company_name_kana: v }))}
                />
                <EditRow label="郵便番号" value={draft.postal_code} onChange={(v) => setDraft((p) => ({ ...p, postal_code: v }))} />
                <EditRow
                  label="住所（市区町村）"
                  value={draft.address_city}
                  onChange={(v) => setDraft((p) => ({ ...p, address_city: v }))}
                />
                <EditRow
                  label="住所（番地以降）"
                  value={draft.address_rest}
                  onChange={(v) => setDraft((p) => ({ ...p, address_rest: v }))}
                />
                <EditRow label="電話番号" value={draft.phone} onChange={(v) => setDraft((p) => ({ ...p, phone: v }))} />
                <EditRow label="FAX番号" value={draft.fax} onChange={(v) => setDraft((p) => ({ ...p, fax: v }))} />
                <EditRow
                  label="設立年月日"
                  value={draft.established_date}
                  onChange={(v) => setDraft((p) => ({ ...p, established_date: v }))}
                />
                <EditRow label="業種" value={draft.industry} onChange={(v) => setDraft((p) => ({ ...p, industry: v }))} />
                <EditRow
                  label="業種（その他）"
                  value={draft.industry_other}
                  onChange={(v) => setDraft((p) => ({ ...p, industry_other: v }))}
                />
                <EditRow label="資本金" value={draft.capital} onChange={(v) => setDraft((p) => ({ ...p, capital: v }))} />
                <EditRow label="店舗数" value={draft.store_count} onChange={(v) => setDraft((p) => ({ ...p, store_count: v }))} />
                <EditRow
                  label="従業員数"
                  value={draft.employee_count}
                  onChange={(v) => setDraft((p) => ({ ...p, employee_count: v }))}
                />
                <EditRow
                  label="営業時間"
                  value={draft.business_hours}
                  onChange={(v) => setDraft((p) => ({ ...p, business_hours: v }))}
                />
                <EditRow
                  label="定休日"
                  value={draft.regular_holiday}
                  onChange={(v) => setDraft((p) => ({ ...p, regular_holiday: v }))}
                />
              </>
            ) : (
  <div
    style={{
      padding: 12,
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 20,
      alignItems: "start",
    }}
  >
    <ReadonlyInputBox label="会社名（全角・編集可）" value={data?.company_name} required />
    <ReadonlyInputBox label="会社名（カナ・半角）" value={data?.company_name_kana} required />

    <ReadonlyInputBox label="郵便番号" value={data?.postal_code} required />
    <ReadonlyInputBox label="電話番号" value={data?.phone} required />

    <ReadonlyInputBox label="登記所在地（都道府県・市区町村・全角）" value={data?.address_city} required />
    <ReadonlyInputBox label="FAX番号" value={data?.fax} />

    <ReadonlyInputBox label="登記所在地（番地以降・全角）" value={data?.address_rest} required />
    <ReadonlyInputBox label="会社設立年月（YYYY/MM）" value={data?.established_date} required />

    <ReadonlyInputBox label="業種（スタッフ設定のため編集不可）" value={data?.industry} required />
    <ReadonlyInputBox
      label="業種（その他）"
      value={data?.industry_other}
      required={data?.industry === "その他"}
    />

    <ReadonlyInputBox label="資本金" value={data?.capital} unit="万円" />
    <ReadonlyInputBox label="店舗数" value={data?.store_count} unit="店舗" />

    <ReadonlyInputBox label="従業員数" value={data?.employee_count} unit="人" />
    <ReadonlyInputBox label="営業時間" value={data?.business_hours} />

    <ReadonlyInputBox label="定休日" value={data?.regular_holiday} />
  </div>
)}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
